/**
 * Central usage-policy layer.
 *
 * ALL free/premium limits live here — never scatter the raw numbers across the
 * codebase. The backend enforces these; the UI reads them to show usage,
 * remaining quota and paywall states.
 */

import type { Role } from './roles.js';

/**
 * There is ONE paid product (Astral Premium) sold in two durations, and the
 * duration carries different quotas:
 *   PREMIUM      — the 1-month entitlement: higher monthly limits, still capped.
 *   PREMIUM_PLUS — the 3-month entitlement: no monthly pack quota at all.
 */
export const PLANS = ['FREE', 'PREMIUM', 'PREMIUM_PLUS'] as const;
export type Plan = (typeof PLANS)[number];

/** The entitlement tier persisted on a user (null on legacy records). */
export type PremiumTier = 'PREMIUM' | 'PREMIUM_PLUS';

export function planFor(isPremium: boolean): Plan {
  return isPremium ? 'PREMIUM' : 'FREE';
}

/**
 * The effective plan used for limit enforcement. OWNER and ADMIN are exempt
 * from all quotas for testing/management; everyone else follows their real
 * premium flag plus the stored tier. Centralized here so limits are never
 * hardcoded per-surface.
 *
 * LEGACY SAFETY: a Premium user with no stored tier predates the two-duration
 * model, and the old single PREMIUM plan had NO monthly pack quota. Such
 * records therefore resolve to PREMIUM_PLUS so nobody is silently downgraded
 * mid-subscription.
 */
export function resolvePlan(role: Role, isPremium: boolean, tier?: PremiumTier | null): Plan {
  if (role === 'OWNER' || role === 'ADMIN') return 'PREMIUM_PLUS';
  if (!isPremium) return 'FREE';
  return tier === 'PREMIUM' ? 'PREMIUM' : 'PREMIUM_PLUS';
}

/** The kind of stickers a project produces. */
export const STICKER_TYPES = ['IMAGE', 'VIDEO', 'MIXED'] as const;
export type StickerType = (typeof STICKER_TYPES)[number];
export const DEFAULT_STICKER_TYPE: StickerType = 'IMAGE';

export function isStickerType(value: unknown): value is StickerType {
  return typeof value === 'string' && (STICKER_TYPES as readonly string[]).includes(value);
}

export interface TypePolicy {
  /** Packs a user may create per calendar month. `null` = unlimited. */
  packsPerMonth: number | null;
  /**
   * Maximum stickers allowed in a single pack.
   *
   * For MIXED this is a COMBINED cap: images + videos together may not exceed
   * it. There is deliberately no separate per-medium sub-limit, because two
   * sub-limits could sum above the stated maximum.
   */
  stickersPerPack: number;
}

/**
 * The highest per-pack capacity Telegram itself supports. Nothing may claim
 * "unlimited stickers per pack" — the platform caps it, so the 3-month plan
 * gets this maximum rather than a promise the API would reject.
 */
export const MAX_STICKERS_PER_PACK = 120;

/** The single source of truth for plan limits. */
const POLICY: Record<Plan, Record<StickerType, TypePolicy>> = {
  FREE: {
    IMAGE: { packsPerMonth: 8, stickersPerPack: 50 },
    VIDEO: { packsPerMonth: 5, stickersPerPack: 30 },
    MIXED: { packsPerMonth: 2, stickersPerPack: 20 },
  },
  // 1-month: clearly better than Free, deliberately still bounded.
  PREMIUM: {
    IMAGE: { packsPerMonth: 12, stickersPerPack: 90 },
    VIDEO: { packsPerMonth: 9, stickersPerPack: 70 },
    MIXED: { packsPerMonth: 6, stickersPerPack: 50 },
  },
  // 3-month: no monthly pack quota, and the maximum supported per-pack capacity.
  PREMIUM_PLUS: {
    IMAGE: { packsPerMonth: null, stickersPerPack: MAX_STICKERS_PER_PACK },
    VIDEO: { packsPerMonth: null, stickersPerPack: MAX_STICKERS_PER_PACK },
    MIXED: { packsPerMonth: null, stickersPerPack: MAX_STICKERS_PER_PACK },
  },
};

export function typePolicy(plan: Plan, type: StickerType): TypePolicy {
  return POLICY[plan][type];
}

export function packsPerMonth(plan: Plan, type: StickerType): number | null {
  return POLICY[plan][type].packsPerMonth;
}

export function stickersPerPack(plan: Plan, type: StickerType): number {
  return POLICY[plan][type].stickersPerPack;
}

/** Per-type usage snapshot returned by the usage endpoint. */
export interface TypeUsage {
  type: StickerType;
  /** Packs created this month. */
  used: number;
  /** Monthly pack limit (null = unlimited). */
  limit: number | null;
  /** Packs remaining (null = unlimited). */
  remaining: number | null;
  /** Max stickers per pack for this plan+type. */
  stickersPerPack: number;
}

export interface UsageSummary {
  plan: Plan;
  /** Start of the current usage window (ISO, first of month UTC). */
  periodStart: string;
  types: TypeUsage[];
}

/** Whether a new pack of `type` may be created given how many exist this month. */
export function canCreatePack(plan: Plan, type: StickerType, usedThisMonth: number): boolean {
  const limit = packsPerMonth(plan, type);
  return limit === null || usedThisMonth < limit;
}

/** Whether another sticker may be added to a pack of `type`. */
export function canAddSticker(plan: Plan, type: StickerType, currentCount: number): boolean {
  return currentCount < stickersPerPack(plan, type);
}

/**
 * Whether a pack of `type` holding `imageCount` images and `videoCount` videos
 * is within its capacity.
 *
 * This is the explicit form of the MIXED rule: the two media are summed and
 * checked against ONE cap, so 12 images + 8 videos fits a 20-total pack while
 * 15 + 10 does not. Single-medium packs simply sum to their own count.
 */
export function packCapacityUsed(imageCount: number, videoCount: number): number {
  return imageCount + videoCount;
}

export function fitsPackCapacity(
  plan: Plan,
  type: StickerType,
  imageCount: number,
  videoCount: number,
): boolean {
  return packCapacityUsed(imageCount, videoCount) <= stickersPerPack(plan, type);
}
